import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  TV_DISPLAY_COLLECTION,
  TV_DISPLAY_DEFAULT_CONFIG,
  TV_DISPLAY_MAIN_DOC,
  TV_DISPLAY_SUBCOLLECTIONS,
} from "./tvDisplayConstants";

export function getTvDisplayDocRef() {
  return doc(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC);
}

export function getTvDisplayMediaCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.media);
}

export function getTvDisplayPlaylistsCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.playlists);
}

export function getTvDisplayCelebrationsCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.celebrations);
}

export function getTvDisplayPlaylistItemsCollectionRef(playlistId) {
  return collection(
    db,
    TV_DISPLAY_COLLECTION,
    TV_DISPLAY_MAIN_DOC,
    TV_DISPLAY_SUBCOLLECTIONS.playlists,
    String(playlistId || ""),
    "items"
  );
}

export function getTvDisplayDevicesCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.devices);
}

export function getTvDisplayPairingsCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.pairings);
}

export function getTvDisplayPublicationsCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.publications);
}

export function getTvDisplayPublicationDocRef(publicationId) {
  return doc(
    db,
    TV_DISPLAY_COLLECTION,
    TV_DISPLAY_MAIN_DOC,
    TV_DISPLAY_SUBCOLLECTIONS.publications,
    String(publicationId || "")
  );
}

export function getTvDisplayPublicationItemsCollectionRef(publicationId) {
  return collection(
    db,
    TV_DISPLAY_COLLECTION,
    TV_DISPLAY_MAIN_DOC,
    TV_DISPLAY_SUBCOLLECTIONS.publications,
    String(publicationId || ""),
    "items"
  );
}

export function getTvDisplayDevicePublishedCurrentDocRef(deviceId) {
  return doc(
    db,
    TV_DISPLAY_COLLECTION,
    TV_DISPLAY_MAIN_DOC,
    TV_DISPLAY_SUBCOLLECTIONS.devices,
    String(deviceId || ""),
    "published",
    "current"
  );
}

export function getTvDisplayCommandsCollectionRef() {
  return collection(db, TV_DISPLAY_COLLECTION, TV_DISPLAY_MAIN_DOC, TV_DISPLAY_SUBCOLLECTIONS.commands);
}

export async function initializeTvDisplayConfig() {
  const ref = getTvDisplayDocRef();

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (snapshot.exists()) return;

    transaction.set(ref, {
      ...TV_DISPLAY_DEFAULT_CONFIG,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  const snapshot = await getDoc(ref);
  return normalizeTvDisplayConfig(snapshot);
}

export async function getTvDisplayConfig() {
  const snapshot = await getDoc(getTvDisplayDocRef());
  if (!snapshot.exists()) {
    return initializeTvDisplayConfig();
  }
  return normalizeTvDisplayConfig(snapshot);
}

export function subscribeToTvDisplayConfig(callback, errorCallback) {
  return onSnapshot(
    getTvDisplayDocRef(),
    async (snapshot) => {
      if (!snapshot.exists()) {
        try {
          const created = await initializeTvDisplayConfig();
          if (typeof callback === "function") callback(created);
        } catch (error) {
          if (typeof errorCallback === "function") errorCallback(error);
        }
        return;
      }

      if (typeof callback === "function") {
        callback(normalizeTvDisplayConfig(snapshot));
      }
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function updateTvDisplayConfig(data = {}) {
  const ref = getTvDisplayDocRef();
  await initializeTvDisplayConfig();
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

function normalizeTvDisplayConfig(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...TV_DISPLAY_DEFAULT_CONFIG,
    ...data,
  };
}
